import React, { useEffect, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid';
import * as yup from 'yup';
import dayjs from 'dayjs';
import * as fileApi from '../../lib/api/file';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { Box, IconButton } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';

import { BuysEstimateListBox } from '../../components/buys_estimate/BuysEstimateListBox';

import {
  listBuysEstimate,
  setBuysEstimateFilters,
  resetBuysEstimateFilters,
} from '../../modules/chanceModule/buys-estimate';
import BuysEstimateList from '../../components/buys_estimate/BuysEstimateList';
import EstimateEditModal from '../../components/buys_estimate/EstimateEditModal';

const BuysEstimateListContainer = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const { files, defaultFilters, error, loading } = useSelector(
    ({ chanceModule, loading }) => ({
      files: chanceModule.buysEstimate.files,
      defaultFilters: chanceModule.buysEstimate.defaultFilters,
      error: chanceModule.buysEstimate.error,
      loading: loading['chance/LIST_Buys_ESTIMATE'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listBuysEstimate(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (files) {
      setTableData(files);
    }
  }, [files]);

  const [showEdit, setShowEdit] = useState(false);
  const [selectedEdit, setSelectedEdit] = useState(null);
  const handleEdit = (param) => {
    setSelectedEdit(param);
    setShowEdit(true);
  };

  const handleEditClose = () => {
    setShowEdit(false);
    setSelectedEdit(null);
  };

  const handleSave = async (changeDate) => {
    try {
      const dateToSubmit = {
        fileUuid: selectedEdit.fileUuid,
        fileAttrVal5: dayjs(changeDate).format('YYYY-MM-DD'),
      };
      const response = await fileApi.editFile(
        selectedEdit.fileUuid,
        dateToSubmit,
      );
      if (response.data.info) {
        dispatch(listBuysEstimate(makeFilters(defaultFilters)));
        enqueueSnackbar('저장 되었습니다', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const downloadFile = (file) => {
    const url = file.fileUrl;

    fetch(url, { method: 'GET' })
      .then((res) => {
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileNm;
        document.body.appendChild(a);
        a.click();
        setTimeout((_) => {
          window.URL.revokeObjectURL(url);
        }, 60000);
        a.remove();
      })
      .catch((err) => {
        console.error('err: ', err);
      });
  };

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'fileUuid',
        headerName: '파일ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'prjUuid',
        headerName: '프로젝트ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'filePartnerUuid',
        headerName: '견적서회사ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'fileRegDtAction',
        headerName: '',
        minWidth: 30,
        flex: 0.2,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="edit"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon />
          </IconButton>
        ),
      },
      {
        field: 'fileRegDt',
        headerName: '받은(수신)일자',
        minWidth: 160,
        flex: 1,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
      },
      {
        field: 'filePartnerNm',
        headerName: '매입처명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },

      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 400,
        flex: 1,
        description: '',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
      {
        field: 'fileNm',
        headerName: '파일명',
        minWidth: 600,
        flex: 1,
        type: 'string',
        editable: false,
        disableClickEventBubbling: true,
        renderCell: (params) => (
          <div>
            {params.row.fileNm}
            {params.row.fileUrl && (
              <IconButton
                color="primary"
                size="small"
                sx={{ mx: 2 }}
                onClick={(e) => downloadFile(params.row)}
              >
                <CloudDownloadIcon />
              </IconButton>
            )}
          </div>
        ),
      },
      {
        field: 'fileCrtNm',
        headerName: '파일등록자',
        minWidth: 200,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
      {
        field: 'fileCrtDt',
        headerName: '파일등록일',
        minWidth: 200,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
    ],
    [],
  );

  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        let searchData = {
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
        };
        dispatch(setBuysEstimateFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="포캐스팅 관리/매입 견적서 현황/매입 견적서 리스트"
        icon="chance"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <BuysEstimateListBox formik={formik} onReset={() => dispatch(resetBuysEstimateFilters())} />
        <BuysEstimateList
          apiRef={apiRef}
          columns={columns}
          rows={tableData}
          error={error}
          loading={loading}
          columnVisibilityModel={{
            // Hide columns status and traderName, the other columns will remain visible
            id: false,
            fileUuid: false,
            prjUuid: false,
            filePartnerUuid: false,
          }}

        />
      </Box>
      {showEdit && (
        <EstimateEditModal
          currentData={selectedEdit}
          open={showEdit}
          handleClose={handleEditClose}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default BuysEstimateListContainer;
